import React from 'react';
import type { ChartArtifactPayload } from '@cotera/api';
import { getChartColorForLabel } from './chart-config';
import { chartPayloadAccentLabel } from './chart-label-colors';
import {
  chartPayloadToSparkBuckets,
  chartPayloadToSparkPoints,
  chartPayloadToSparkValues,
  isBarLikeChartType,
  isLineLikeChartType,
  sparkPointsToAreaPath,
  sparkPointsToPolyline,
  SPARK_VIEW_HEIGHT,
  SPARK_VIEW_WIDTH,
  type SparkBarBucket,
} from './chart-spark-utils';

const SPARK_BAR_BORDER_RADIUS = 1.5;

function breakpointSparkIndexes(payload: ChartArtifactPayload): number[] {
  const breakpoints = payload.breakpoints ?? [];
  return breakpoints
    .map((breakpoint) => {
      if (breakpoint.x_label !== null && breakpoint.x_label !== '') {
        const index = payload.data.labels.indexOf(breakpoint.x_label);
        return index === -1 ? null : index;
      }
      if (
        breakpoint.label_index !== null &&
        Number.isInteger(breakpoint.label_index) &&
        breakpoint.label_index >= 0 &&
        breakpoint.label_index < payload.data.labels.length
      ) {
        return breakpoint.label_index;
      }
      return null;
    })
    .filter((index): index is number => index !== null);
}

/** Renders spark geometry in the standard 100×40 viewBox (for use inside scaled groups). */
export const SparkShapes: React.FC<{
  payload: ChartArtifactPayload;
}> = ({ payload }) => {
  const accentColor = getChartColorForLabel(chartPayloadAccentLabel(payload));

  if (payload.chart_type === 'pie') {
    const values = chartPayloadToSparkValues(payload);
    const total = values.reduce((sum, value) => sum + value, 0);
    if (total === 0) {
      return null;
    }
    let currentX = 0;
    const gap = 0.5;
    return (
      <>
        {values.map((value, index) => {
          const segmentWidth = Math.max(
            (value / total) * SPARK_VIEW_WIDTH -
              (index < values.length - 1 ? gap : 0),
            1
          );
          const sliceLabel = payload.data.labels[index] ?? '';
          const rect = (
            <rect
              key={index}
              x={currentX}
              y={0}
              width={segmentWidth}
              height={SPARK_VIEW_HEIGHT}
              fill={getChartColorForLabel(sliceLabel)}
              rx={SPARK_BAR_BORDER_RADIUS}
            />
          );
          currentX += segmentWidth + gap;
          return rect;
        })}
      </>
    );
  }

  if (isBarLikeChartType(payload.chart_type)) {
    const buckets = chartPayloadToSparkBuckets(payload);
    if (buckets.length === 0) {
      return null;
    }
    const max = Math.max(...buckets.map((bucket) => bucket.count));
    const slotWidth = SPARK_VIEW_WIDTH / buckets.length;
    const gap = 0.8;
    return (
      <>
        {buckets.map((bucket: SparkBarBucket, index) => {
          const barHeight =
            max === 0 ? 0 : (bucket.count / max) * SPARK_VIEW_HEIGHT;
          const barWidth = Math.max(slotWidth - gap, 0.5);
          const x = index * slotWidth + gap / 2;
          const y = SPARK_VIEW_HEIGHT - barHeight;
          return (
            <rect
              key={index}
              x={x}
              y={y}
              width={barWidth}
              height={barHeight}
              fill={accentColor}
              rx={SPARK_BAR_BORDER_RADIUS}
            />
          );
        })}
      </>
    );
  }

  if (isLineLikeChartType(payload.chart_type)) {
    const points = chartPayloadToSparkPoints(payload);
    if (points.length === 0) {
      return null;
    }
    const polyline = sparkPointsToPolyline(points);
    const isArea = payload.chart_type === 'area';
    const areaPath = isArea ? sparkPointsToAreaPath(points) : null;
    const isScatter = payload.chart_type === 'scatter';

    return (
      <>
        {areaPath !== null && areaPath !== '' && (
          <path
            d={areaPath}
            fill={accentColor}
            fillOpacity={0.2}
            stroke="none"
          />
        )}
        {isScatter ? (
          points.map((point, index) => (
            <circle
              key={index}
              cx={point.x * SPARK_VIEW_WIDTH}
              cy={point.y * SPARK_VIEW_HEIGHT}
              r={1.2}
              fill={accentColor}
            />
          ))
        ) : (
          <polyline
            points={polyline}
            fill="none"
            stroke={accentColor}
            strokeWidth={1.5}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}
        {breakpointSparkIndexes(payload).map((index) => {
          const point = points[index];
          if (point === undefined) {
            return null;
          }
          return (
            <circle
              key={`breakpoint-${index}`}
              cx={point.x * SPARK_VIEW_WIDTH}
              cy={point.y * SPARK_VIEW_HEIGHT}
              r={2}
              fill="var(--background)"
              stroke={accentColor}
              strokeWidth={1}
            />
          );
        })}
      </>
    );
  }

  return null;
};
